"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GaugeVoteWrapper = void 0;
const solana_contrib_1 = require("@saberhq/solana-contrib");
const web3_js_1 = require("@solana/web3.js");
const utf8_1 = require("@project-serum/anchor/dist/cjs/utils/bytes/utf8");
const GaugeVoter_1 = require("./GaugeVoter");
const Gauge_1 = require("./Gauge");
const bn_js_1 = __importDefault(require("bn.js"));
class GaugeVoteWrapper {
    constructor(address, gaugeVoter, gauge) {
        this.address = address;
        this.gaugeVoter = gaugeVoter;
        this.gauge = gauge;
        this._data = null;
    }
    static addressWithBump(gaugeVoter, gauge) {
        return web3_js_1.PublicKey.findProgramAddress([
            (0, utf8_1.encode)('GaugeVote'),
            gaugeVoter.address.toBuffer(),
            gauge.address.toBuffer(),
        ], gaugeVoter.program.programId);
    }
    static async address(gaugeVoter, gauge) {
        const [address] = await GaugeVoteWrapper.addressWithBump(gaugeVoter, gauge);
        return address;
    }
    static async new(gaugeVoter, gauge) {
        if (gaugeVoter.sdk !== gauge.sdk) {
            throw new Error('SDK object mismatch');
        }
        const address = await GaugeVoteWrapper.address(gaugeVoter, gauge);
        return new GaugeVoteWrapper(address, gaugeVoter, gauge);
    }
    static async read(sdk, address) {
        const data = await sdk.program.account.gaugeVote.fetch(address);
        const result = new GaugeVoteWrapper(address, await GaugeVoter_1.GaugeVoterWrapper.read(sdk, data.gaugeVoter), new Gauge_1.GaugeWrapper(sdk, data.gauge));
        result._data = data;
        return result;
    }
    get sdk() {
        return this.gaugeVoter.sdk;
    }
    get provider() {
        return this.sdk.provider;
    }
    get program() {
        return this.sdk.program;
    }
    async reload() {
        this._data = await this.program.account.gaugeVote.fetch(this.address);
        return this._data;
    }
    async tryReload() {
        this._data = await this.program.account.gaugeVote.fetchNullable(this.address);
        return this._data;
    }
    async data() {
        if (!this._data) {
            return await this.reload();
        }
        return this._data;
    }
    async tryData() {
        if (!this._data) {
            return await this.tryReload();
        }
        return this._data;
    }
    get escrow() {
        return this.gaugeVoter.escrow;
    }
    async gaugemeister() {
        return this.gauge.gaugemeister();
    }
    async create({ nftToken, nftOwner, rentPayer, }) {
        const signers = [];
        if (rentPayer) {
            signers.push(rentPayer);
        }
        if (nftOwner) {
            signers.push(nftOwner);
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .createGaugeVote()
                .accounts({
                gaugeVote: this.address,
                controlEscrow: {
                    escrow: this.escrow.address,
                    nftMint: (await this.escrow.data()).nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                },
                gaugeVoter: this.gaugeVoter.address,
                gauge: this.gauge.address,
                gaugemeister: (await this.gaugemeister()).address,
                rentPayer: (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || this.provider.wallet.publicKey,
                systemProgram: web3_js_1.SystemProgram.programId,
            })
                .instruction(),
        ], signers);
    }
    async setVote({ newWeight, nftToken, nftOwner, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .gaugeSetVote(newWeight)
                .accounts({
                gaugeVote: this.address,
                gaugeVoter: this.gaugeVoter.address,
                controlEscrow: {
                    escrow: this.escrow.address,
                    nftMint: (await this.escrow.data()).nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                },
                gauge: this.gauge.address,
                gaugemeister: (await this.gaugemeister()).address,
            })
                .instruction(),
        ], signers);
    }
    async close({ nftToken, nftOwner, resetWeight = true, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        let tx = new solana_contrib_1.TransactionEnvelope(this.provider, []);
        if (resetWeight) {
            tx = tx.combine(await this.setVote({
                newWeight: new bn_js_1.default(0),
                nftToken,
                nftOwner,
            }));
        }
        return tx.combine(new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .closeGaugeVote()
                .accounts({
                gaugeVote: this.address,
                controlEscrow: {
                    escrow: this.escrow.address,
                    nftMint: (await this.escrow.data()).nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                },
                gaugeVoter: this.gaugeVoter.address,
                gauge: this.gauge.address,
                nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                gaugemeister: (await this.gaugemeister()).address,
            })
                .instruction(),
        ], signers));
    }
    async closeDead({ rentCollector }) {
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .closeDeadGaugeVote()
                .accounts({
                gaugeVote: this.address,
                escrow: this.escrow.address,
                nftMint: (await this.escrow.data()).nftMint,
                gaugeVoter: this.gaugeVoter.address,
                gaugemeister: (await this.gaugemeister()).address,
                gauge: this.gauge.address,
                rentCollector: rentCollector || this.provider.walletKey,
            })
                .instruction(),
        ]);
    }
    static async loadByGauge(gauge) {
        const votes = await gauge.program.account.gaugeVote.all([
            { memcmp: { bytes: gauge.address.toBase58(), offset: 40 } },
        ]);
        return await Promise.all(votes.map(async (vote) => {
            const wrapper = new GaugeVoteWrapper(vote.publicKey, await GaugeVoter_1.GaugeVoterWrapper.read(gauge.sdk, vote.account.gaugeVoter), new Gauge_1.GaugeWrapper(gauge.sdk, vote.account.gauge));
            wrapper._data = vote.account;
            return wrapper;
        }));
    }
}
exports.GaugeVoteWrapper = GaugeVoteWrapper;
GaugeVoteWrapper.SPACE = 80 + 32;
//# sourceMappingURL=GaugeVote.js.map